import React, { useEffect, useMemo, useRef, useState } from 'react';
import { confirmAction } from '../../utils/confirm';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';import { Zone } from '../../types';
import { formatInTZ } from '../../utils/timezone';
import { ZoneDevicePicker, PropertyPicker } from './SurveillancePickers';
import {
    SurveillanceDevice, sectionStyle, btnStyle, inputStyle, labelStyle,
    CHART_COLORS, PROPERTY_LABELS, formatDate, parseSeriesKey, makeSeriesKey, PropertyTemplate,
} from './surveillanceShared';
import { useMonitorState } from './hooks/useMonitorState';
import { fetchSetpoints } from '../../services/setpointsApi';
import { computeDeviation, formatDeviation } from '../../utils/deviation';
interface MonitorTabProps {
    zones: Zone[];
    devices: SurveillanceDevice[];
    timezone: string | null | undefined;
    /** Sant när fliken visas. Fliken hålls monterad så att urvalet finns kvar vid flikbyte. */
    active: boolean;
    /** Visar fel i sidans röda ruta. */
    onError: (message: string) => void;
}

// ── Template Picker ──────────────────────────────────────────────────────────

interface TemplatePickerProps {
    templates: PropertyTemplate[];
    selectedProperties: Set<string>;
    selectedDevices: Set<string>;
    /** Human-readable hint shown on the save button ("device type" or "multi-device") */
    saveTypeLabel: string;
    propLabel: (p: string) => string;
    onSave: (name: string) => void;
    onApply: (id: number) => void;
    onDelete: (id: number) => void;
}

const TemplatePicker: React.FC<TemplatePickerProps> = ({
    templates, selectedProperties, selectedDevices, saveTypeLabel, propLabel, onSave, onApply, onDelete,
}) => {
    const [saving, setSaving] = useState(false);
    const [name, setName] = useState('');
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const canSave = selectedProperties.size > 0 && selectedDevices.size > 0;

    const handleSave = () => {
        const trimmed = name.trim();
        if (!trimmed || !canSave) return;
        onSave(trimmed);
        setName('');
        setSaving(false);
    };

    const detailChip: React.CSSProperties = {
        display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
        fontSize: '11px', backgroundColor: '#f3f4f6', color: '#374151',
        border: '1px solid #e5e7eb', marginRight: '4px', marginBottom: '4px',
    };

    return (
        <div style={{ ...sectionStyle, padding: '12px 16px', marginTop: '-16px', marginBottom: '16px', backgroundColor: '#fefce8', border: '1px solid #fde68a' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <label style={{ ...labelStyle, margin: 0, fontSize: '13px' }}>Mallar</label>

                {templates.map(t => {
                    const devCount = t.devices?.length ?? 0;
                    const isExpanded = expandedId === t.id;
                    const typeBadge = t.template_type === 'device_type'
                        ? { label: t.device_type ?? 'enhet', color: '#1d4ed8', bg: '#eff6ff' }
                        : { label: 'flera', color: '#6d28d9', bg: '#f5f3ff' };
                    return (
                        <span key={t.id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            padding: '3px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: 600,
                            border: `1.5px solid ${isExpanded ? '#b45309' : '#d97706'}`,
                            backgroundColor: isExpanded ? '#fef3c7' : '#fffbeb', color: '#92400e',
                            cursor: 'pointer', userSelect: 'none',
                        }}>
                            <span style={{
                                fontSize: '10px', fontWeight: 700, padding: '0px 5px', borderRadius: '8px',
                                backgroundColor: typeBadge.bg, color: typeBadge.color,
                                border: `1px solid ${typeBadge.color}40`, marginRight: '2px',
                            }}>{typeBadge.label}</span>
                            <span onClick={() => onApply(t.id)}
                                title="Klicka för att använda den här mallen">
                                {t.name} ({t.properties.length} mätvärden{devCount > 0 ? ` \u00b7 ${devCount} enheter` : ''})
                            </span>
                            <span onClick={() => setExpandedId(isExpanded ? null : t.id)}
                                style={{ marginLeft: '2px', cursor: 'pointer', fontSize: '12px', lineHeight: 1, opacity: 0.7 }}
                                title="Visa detaljer"
                            >{isExpanded ? '\u25be' : '\u24d8'}</span>
                            <span onClick={() => { if (confirmAction(`Ta bort mallen "${t.name}"?`)) onDelete(t.id); }}
                                style={{ marginLeft: '2px', color: '#b91c1c', cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}
                                title="Ta bort mallen"
                            >×</span>
                        </span>
                    );
                })}

                {saving ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <input
                            style={{ ...inputStyle, width: '160px', padding: '4px 8px', fontSize: '12px' }}
                            value={name}
                            onChange={e => setName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setSaving(false); }}
                            placeholder="Namn på mallen"
                            autoFocus
                        />
                        <button onClick={handleSave} disabled={!name.trim() || !canSave}
                            style={btnStyle(name.trim() && canSave ? '#059669' : '#aaa')}>Spara</button>
                        <button onClick={() => setSaving(false)} style={btnStyle('#6b7280')}>Avbryt</button>
                    </span>
                ) : (
                    <button onClick={() => setSaving(true)}
                        disabled={!canSave}
                        style={btnStyle(canSave ? '#d97706' : '#aaa')}
                        title={`Spara valda enheter och mätvärden som en mall (${saveTypeLabel})`}
                    >+ Spara som mall <span style={{ fontSize: '10px', fontWeight: 400, opacity: 0.8 }}>({saveTypeLabel})</span></button>
                )}
            </div>

            {expandedId !== null && (() => {
                const t = templates.find(tpl => tpl.id === expandedId);
                if (!t) return null;
                return (
                    <div style={{ marginTop: '10px', padding: '10px 12px', backgroundColor: '#fff',
                        borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                        <div style={{ marginBottom: '6px' }}>
                            <strong style={{ color: '#92400e' }}>Typ: </strong>
                            {t.template_type === 'device_type'
                                ? <span style={{ color: '#1d4ed8' }}>Enhetstyp — {t.device_type}</span>
                                : <span style={{ color: '#6d28d9' }}>Flera enheter</span>
                            }
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <strong style={{ color: '#92400e' }}>Enheter ({t.devices?.length ?? 0}):</strong>
                            <div style={{ marginTop: '4px' }}>
                                {(t.devices && t.devices.length > 0)
                                    ? t.devices.map(d => <span key={d.id} style={detailChip}>{d.name}</span>)
                                    : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Inga kopplade enheter</span>
                                }
                            </div>
                        </div>
                        <div>
                            <strong style={{ color: '#92400e' }}>Mätvärden ({t.properties.length}):</strong>
                            <div style={{ marginTop: '4px' }}>
                                {t.properties.map(p => <span key={p} style={detailChip}>{propLabel(p)}</span>)}
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

const MonitorTab: React.FC<MonitorTabProps> = ({ zones, devices, timezone, active, onError }) => {
    const {
        filteredDevices, selectedDevices, selectedZone,
        setSelectedZone: onZoneChange, toggleDevice: onToggleDevice,
        selectAllDevices: onSelectAllDevices, clearAllDevices: onClearDevices,
        selectedProperties, availableMonitorProperties, monitorPropsLoading,
        propLabel, propNameMap, devicePropertiesMap,
        toggleProperty: onToggleProperty, selectAllProperties: onSelectAllProperties, clearAllProperties: onClearProperties,
        templates, handleSaveTemplate: onSaveTemplate, handleApplyTemplate: onApplyTemplate, handleDeleteTemplate: onDeleteTemplate,
        monitorRows, monitorLoading, monitorError, loadMonitorData: onLoadMonitorData,
        logFrom, logTo, setLogFrom: onLogFromChange, setLogTo: onLogToChange,
        historyLoading, historyError, historyData, loadHistoryData: onLoadHistoryData,
        chartData, chartSeries, chartDisplayMap, chartEnumSeries, chartEnumLabels,
    } = useMonitorState({ zones, devices, active, onError });
    const pendingExportRef = useRef(false);
    const [chartFullscreen, setChartFullscreen] = useState(false);
    const fullscreenOverlayRef = useRef<HTMLDivElement | null>(null);

    // ── Setpoints / deviation (avvikelse) ────────────────────────────────────
    // Saved targets keyed by seriesKey (ieee::encodedProperty).
    const [savedTargets, setSavedTargets] = useState<Record<string, number>>({});
    // Editable per-series target overrides (what-if). String so the user can type freely.
    const [targetOverrides, setTargetOverrides] = useState<Record<string, string>>({});

    // Load saved setpoints once.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const sps = await fetchSetpoints();
                if (cancelled) return;
                const map: Record<string, number> = {};
                for (const sp of sps) map[makeSeriesKey(sp.ieee_address, sp.parameter_key)] = sp.target_value;
                setSavedTargets(map);
            } catch { /* setpoints optional */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Prefill the editable target for any newly-charted series that has a saved value.
    useEffect(() => {
        setTargetOverrides(prev => {
            const next = { ...prev };
            let changed = false;
            for (const key of chartSeries) {
                if (next[key] === undefined && savedTargets[key] !== undefined) {
                    next[key] = String(savedTargets[key]);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [chartSeries, savedTargets]);

    // Numeric series only (enum series have no meaningful percentage deviation).
    const numericSeries = useMemo(
        () => chartSeries.filter(k => !chartEnumSeries.has(k)),
        [chartSeries, chartEnumSeries]
    );

    // value_num samples per series, from the loaded history.
    const seriesValues = useMemo(() => {
        const map: Record<string, number[]> = {};
        for (const row of historyData) {
            const key = makeSeriesKey(row.ieee_address, row.property);
            if (row.value_num === null || row.value_num === undefined) continue;
            (map[key] ??= []).push(Number(row.value_num));
        }
        return map;
    }, [historyData]);

    // Try to lock to landscape when chart goes fullscreen (Android/Chrome only;
    // iOS Safari blocks orientation.lock — the rotate-hint covers that case).
    useEffect(() => {
        if (!chartFullscreen) return;
        const el = fullscreenOverlayRef.current;
        if (!el) return;

        let didRequestFullscreen = false;
        (async () => {
            try {
                if (el.requestFullscreen) {
                    await el.requestFullscreen();
                    didRequestFullscreen = true;
                } else if ((el as any).webkitRequestFullscreen) {
                    (el as any).webkitRequestFullscreen();
                    didRequestFullscreen = true;
                }
            } catch { /* user gesture missing or unsupported */ }

            try {
                const orientation = (window.screen as any)?.orientation;
                if (orientation?.lock) {
                    await orientation.lock('landscape');
                }
            } catch { /* iOS / desktop / locked by system — ignore */ }
        })();

        // Close overlay if user exits fullscreen via OS gesture (e.g. ESC, swipe)
        const onFsChange = () => {
            if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
                setChartFullscreen(false);
            }
        };
        document.addEventListener('fullscreenchange', onFsChange);
        document.addEventListener('webkitfullscreenchange', onFsChange);

        return () => {
            document.removeEventListener('fullscreenchange', onFsChange);
            document.removeEventListener('webkitfullscreenchange', onFsChange);
            try { (window.screen as any)?.orientation?.unlock?.(); } catch {}
            if (didRequestFullscreen) {
                try {
                    if (document.fullscreenElement && document.exitFullscreen) {
                        document.exitFullscreen();
                    } else if ((document as any).webkitFullscreenElement && (document as any).webkitExitFullscreen) {
                        (document as any).webkitExitFullscreen();
                    }
                } catch {}
            }
        };
    }, [chartFullscreen]);

    // Determine what template type will be saved based on selected devices
    const saveTypeLabel = React.useMemo(() => {
        if (selectedDevices.size === 0) return 'flera enheter';
        const types = new Set(
            Array.from(selectedDevices)
                .map(id => devices.find(d => d.id === id)?.type)
                .filter(Boolean)
        );
        return types.size === 1 ? `enhetstyp: ${Array.from(types)[0]}` : 'flera enheter';
    }, [selectedDevices, devices]);

    // When a pending export was triggered, fire once history loading finishes
    useEffect(() => {
        if (!historyLoading && pendingExportRef.current) {
            pendingExportRef.current = false;
            doExportForAI();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyLoading]);

    // Always-current ref so doExportForAI (called from an effect) never reads stale historyData
    const historyDataRef = useRef(historyData);
    historyDataRef.current = historyData;

    // Open an HTML string in a new popup window. Uses a Blob URL rather than the
    // deprecated document.write(): a written about:blank popup renders empty in
    // modern browsers (COOP / async about:blank reload), which is why the export
    // windows came up blank. Navigating to a blob: URL renders reliably.
    const openHtmlWindow = (html: string, width: number, height: number) => {
        const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        const win = window.open(url, '_blank', `width=${width},height=${height},scrollbars=yes`);
        if (!win) {
            URL.revokeObjectURL(url);
            alert('Tillåt popup-fönster i webbläsaren för att kunna exportera.');
            return;
        }
        // Give the popup time to load before releasing the blob.
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    };

    const exportDeviceInfo = () => {
        const lines: string[] = [];
        lines.push('DevIoT — export av enheter och mätvärden');
        lines.push(`Skapad: ${new Date().toLocaleString('sv-SE')}`);
        lines.push('');
        for (const ieee of Array.from(selectedDevices).sort()) {
            const device = devices.find(d => d.id === ieee);
            const deviceName = device?.name ?? ieee;
            lines.push(`Enhet: ${deviceName}`);
            lines.push(`  IEEE: ${ieee}`);
            const props = devicePropertiesMap[ieee];
            if (props && props.length > 0) {
                lines.push('  Mätvärden:');
                for (const key of [...props].sort()) {
                    const label = propLabel(key, ieee);
                    lines.push(`    ${key.padEnd(36)} ${label}`);
                }
            } else {
                lines.push('  Mätvärden: (inga hämtade ännu)');
            }
            lines.push('');
        }
        const text = lines.join('\n');
        const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        openHtmlWindow(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>DevIoT – enhetsinformation</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: #1e1e1e; color: #d4d4d4; font-family: 'Consolas', 'Courier New', monospace; font-size: 13px; line-height: 1.6; }
    .toolbar { position: sticky; top: 0; z-index: 10; background: #252526; padding: 10px 20px; border-bottom: 1px solid #3d3d3d; display: flex; gap: 12px; align-items: center; }
    .btn { padding: 7px 16px; border: none; border-radius: 4px; cursor: pointer; font-weight: bold; font-size: 13px; }
    .btn-copy { background: #17a2b8; color: #fff; }
    .btn-copy:hover { background: #138496; }
    .hint { color: #888; font-size: 12px; }
    pre { padding: 20px; white-space: pre; overflow-x: auto; }
  </style>
</head>
<body>
  <div class="toolbar">
    <button class="btn btn-copy" onclick="copyAll(this)">&#x1F4CB; Kopiera allt</button>
    <span class="hint">Enhetsnamn och nycklar för de valda enheterna</span>
  </div>
  <pre id="content">${escaped}</pre>
  <script>
    function copyAll(btn) {
      var text = document.getElementById('content').textContent;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(function() {
          btn.textContent = '\u2713 Kopierat!'; setTimeout(function() { btn.textContent = '\uD83D\uDCCB Kopiera allt'; }, 2000);
        });
      } else {
        var ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
        btn.textContent = '\u2713 Kopierat!'; setTimeout(function() { btn.textContent = '\uD83D\uDCCB Kopiera allt'; }, 2000);
      }
    }
  </script>
</body>
</html>`, 900, 640);
    };

    const doExportForAI = () => {
        if (selectedDevices.size === 0) return;
        const rows = historyDataRef.current;
        const tz = timezone || 'UTC';
        const dtFmt = new Intl.DateTimeFormat('sv-SE', {
            timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
        const fmtTs = (ts: string | number | Date): string => {
            const d = new Date(ts as any);
            return isNaN(d.getTime()) ? String(ts) : dtFmt.format(d).replace(', ', ' ');
        };
        const paramId = (property: string): string =>
            property.startsWith('p_') ? property.slice(2) : property;
        const stripZeros = (v: string | number): string => {
            const s = String(v);
            return /^-?\d+\.\d+$/.test(s) ? s.replace(/\.?0+$/, '') : s;
        };
        const multiDevice = selectedDevices.size > 1;

        // col key = "ieee::property" for uniqueness across devices
        interface ColInfo { id: string; label: string; }
        const colOrder: string[] = [];
        const colInfoMap: Record<string, ColInfo> = {};
        const tsMap = new Map<string, Record<string, string>>();

        const ensureCol = (ieee: string, property: string, paramName: string, deviceName: string) => {
            const colKey = `${ieee}::${property}`;
            if (!colInfoMap[colKey]) {
                colOrder.push(colKey);
                const label = multiDevice ? `${deviceName} - ${paramName}` : paramName;
                colInfoMap[colKey] = { id: paramId(property), label };
            }
            return colKey;
        };

        const addPoint = (ts: string, colKey: string, value: string | number, unit: string) => {
            if (!tsMap.has(ts)) tsMap.set(ts, {});
            tsMap.get(ts)![colKey] = value !== '' ? `${stripZeros(value)}${unit}` : '';
        };

        // Use historical data if available, otherwise current readings
        if (rows.length > 0) {
            for (const row of rows) {
                if (!selectedDevices.has(row.ieee_address)) continue;
                if (!selectedProperties.has(row.property)) continue;
                const devName = devices.find(d => d.id === row.ieee_address)?.name ?? row.ieee_address;
                const paramName = row.parameter_name || propLabel(row.property, row.ieee_address);
                const colKey = ensureCol(row.ieee_address, row.property, paramName, devName);
                const val = row.value_num !== null ? row.value_num : (row.value_str ?? '');
                addPoint(fmtTs(row.timestamp), colKey, val, row.parameter_unit ?? '');
            }
        } else {
            for (const row of monitorRows) {
                const paramName = row.propName || propLabel(row.property, row.ieee);
                const colKey = ensureCol(row.ieee, row.property, paramName, row.name);
                const val = row.numericValue !== null ? row.numericValue : row.displayStr;
                addPoint(fmtTs(row.timestamp), colKey, val, row.unit);
            }
        }

        const lines: string[] = [];
        // Header row
        const headers = colOrder.map(k => `(${colInfoMap[k].id})${colInfoMap[k].label}`);
        lines.push(['Timestamp', ...headers].join(';'));

        if (colOrder.length === 0) {
            lines.push('');
            lines.push('Ingen data hittades. Hämta diagramdata först (välj en period och klicka på "Visa diagram"),');
            lines.push('eller kontrollera att enheter och mätvärden är valda.');
        } else {
            // Data rows sorted by timestamp
            for (const [ts, valMap] of [...tsMap.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
                lines.push([ts, ...colOrder.map(k => valMap[k] ?? '')].join(';'));
            }
        }

        const text = lines.join('\n');
        const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        openHtmlWindow(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>DevIoT – export</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: #1e1e1e; color: #d4d4d4; font-family: 'Consolas', 'Courier New', monospace; font-size: 13px; line-height: 1.6; }
    .toolbar { position: sticky; top: 0; z-index: 10; background: #252526; padding: 10px 20px; border-bottom: 1px solid #3d3d3d; display: flex; gap: 12px; align-items: center; }
    .btn { padding: 7px 16px; border: none; border-radius: 4px; cursor: pointer; font-weight: bold; font-size: 13px; }
    .btn-copy { background: #0078d4; color: #fff; }
    .btn-copy:hover { background: #106ebe; }
    .hint { color: #888; font-size: 12px; }
    pre { padding: 20px; white-space: pre; overflow-x: auto; }
  </style>
</head>
<body>
  <div class="toolbar">
    <button class="btn btn-copy" onclick="copyAll(this)">&#x1F4CB; Kopiera allt</button>
    <span class="hint">Klistra in direkt i ChatGPT, Gemini, Copilot eller någon annan AI-assistent</span>
  </div>
  <pre id="content">${escaped}</pre>
  <script>
    function copyAll(btn) {
      var text = document.getElementById('content').textContent;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(function() {
          btn.textContent = '\u2713 Kopierat!'; setTimeout(function() { btn.textContent = '\uD83D\uDCCB Kopiera allt'; }, 2000);
        });
      } else {
        var ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
        btn.textContent = '\u2713 Kopierat!'; setTimeout(function() { btn.textContent = '\uD83D\uDCCB Kopiera allt'; }, 2000);
      }
    }
  </script>
</body>
</html>`, 980, 720);
    };

    const exportForAI = () => {
        if (selectedDevices.size === 0) return;
        if (historyData.length === 0 && monitorRows.length === 0) {
            // No data loaded yet — trigger a history load, then export once done
            pendingExportRef.current = true;
            onLoadHistoryData();
        } else {
            doExportForAI();
        }
    };

    // Adaptive X-axis tick format based on the visible span. For short ranges
    // (≤ 6h) show H:M:S with no date so sub-minute data doesn't collapse into
    // identical minute-only labels; otherwise show date + H:M.
    const chartTickFormatter = useMemo(() => {
        const span = chartData.length > 1
            ? (chartData[chartData.length - 1].ts as number) - (chartData[0].ts as number)
            : 0;
        const opts: Intl.DateTimeFormatOptions = span > 0 && span <= 6 * 3600_000
            ? { year: undefined, month: undefined, day: undefined, hour: '2-digit', minute: '2-digit', second: '2-digit' }
            : { year: undefined, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: undefined };
        return (ts: number) => formatInTZ(ts, timezone, opts);
    }, [chartData, timezone]);

    const renderLineChart = () => (
        <LineChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
                dataKey="ts"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={ts => chartTickFormatter(ts as number)}
                tick={{ fontSize: 11 }}
                minTickGap={40}
            />
            <YAxis yAxisId="left" width={72} tick={{ fontSize: 11 }} />
            {chartEnumSeries.size > 0 && (
                <YAxis
                    yAxisId="right"
                    orientation="right"
                    width={120}
                    tick={{ fontSize: 10 }}
                    domain={[-0.5, chartEnumLabels.length - 0.5]}
                    ticks={chartEnumLabels.map((_, i) => i)}
                    tickFormatter={(val: number) => chartEnumLabels[val] ?? ''}
                    allowDecimals={false}
                />
            )}
            <Tooltip
                labelFormatter={ts => formatInTZ(ts as number, timezone)}
                formatter={(value: any, name: string | undefined, props: any) => {
                    const { ieee, property: prop } = parseSeriesKey(name ?? '');
                    const label = propLabel(prop, ieee);
                    const ts = props?.payload?.ts;
                    const displayStr = ts ? chartDisplayMap[`${ts}::${name}`] : undefined;
                    if (chartEnumSeries.has(name ?? '') && displayStr) {
                        return [displayStr, label];
                    }
                    const formatted = displayStr && displayStr !== String(value)
                        ? `${displayStr} (${value})`
                        : typeof value === 'number'
                            ? value.toLocaleString(undefined, { maximumFractionDigits: 3 })
                            : String(value);
                    return [formatted, label];
                }}
                contentStyle={{ fontSize: '12px' }}
            />
            <Legend
                formatter={(name: string) => {
                    const { ieee, property: prop } = parseSeriesKey(name);
                    const deviceName = devices.find(d => d.id === ieee)?.name ?? ieee;
                    return `${deviceName} · ${propLabel(prop, ieee)}`;
                }}
                wrapperStyle={{ fontSize: '12px' }}
            />
            {chartSeries.slice(0, 10).map((key, i) => (
                <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    yAxisId={chartEnumSeries.has(key) ? 'right' : 'left'}
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    dot={chartData.length < 20 ? { r: 3 } : false}
                    strokeWidth={2}
                    connectNulls={true}
                />
            ))}
        </LineChart>
    );

    return (
        <div>
            {/* ── Zone + Device picker ─────────────────────────── */}
            <ZoneDevicePicker
                zones={zones}
                selectedZone={selectedZone}
                onZoneChange={onZoneChange}
                devices={filteredDevices}
                selectedDevices={selectedDevices}
                onToggleDevice={onToggleDevice}
                onSelectAll={onSelectAllDevices}
                onClear={onClearDevices}
                extraActions={<>
                    <button type="button" onClick={onLoadMonitorData} style={btnStyle('#17a2b8')} title="Hämta nya värden nu">↺ Uppdatera</button>
                </>}
            />

            {/* ── Property picker ──────────────────────────────────── */}
            {selectedDevices.size > 0 && (
                <>
                <PropertyPicker
                    available={availableMonitorProperties}
                    selected={selectedProperties}
                    loading={monitorPropsLoading}
                    propLabel={propLabel}
                    propNameMap={propNameMap}
                    onToggle={onToggleProperty}
                    onSelectAll={onSelectAllProperties}
                    onClear={onClearProperties}
                />
                <TemplatePicker
                    templates={templates}
                    selectedProperties={selectedProperties}
                    selectedDevices={selectedDevices}
                    saveTypeLabel={saveTypeLabel}
                    propLabel={propLabel}
                    onSave={onSaveTemplate}
                    onApply={onApplyTemplate}
                    onDelete={onDeleteTemplate}
                />
                </>
            )}

            {/* ── Monitor table ────────────────────────────────────── */}
            {selectedDevices.size === 0 ? (
                <div style={{ ...sectionStyle, textAlign: 'center', color: '#888', padding: '48px 20px' }}>
                    Välj en eller flera enheter ovanför för att börja.
                </div>
            ) : monitorLoading && monitorRows.length === 0 ? (
                <div style={{ ...sectionStyle, textAlign: 'center', color: '#888', padding: '48px 20px' }}>
                    Hämtar…
                </div>
            ) : monitorError ? (
                <div style={{ ...sectionStyle, textAlign: 'center', color: '#c00', padding: '48px 20px' }}>
                    {monitorError}
                </div>
            ) : monitorRows.length === 0 ? (
                <div style={{ ...sectionStyle, textAlign: 'center', color: '#888', padding: '48px 20px' }}>
                    Ingen data hittades för de valda enheterna och mätvärdena.
                </div>
            ) : (
                <div style={sectionStyle}>
                    <div className="tbl-scroll">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f8f9fa' }}>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Enhet</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Mätvärde</th>
                                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Värde</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Enhet</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Senast uppdaterat</th>
                            </tr>
                        </thead>
                        <tbody>
                            {monitorRows.map((r, i) => {
                                const isNewDevice = i === 0 || monitorRows[i - 1].ieee !== r.ieee;
                                return (
                                    <tr key={`${r.ieee}-${r.property}`} style={{
                                        borderBottom: '1px solid #eee',
                                        backgroundColor: isNewDevice ? '#fafcff' : 'white',
                                    }}>
                                        <td style={{ padding: '8px 12px', fontWeight: isNewDevice ? 'bold' : 'normal', color: isNewDevice ? '#333' : '#999' }}>
                                            {isNewDevice ? r.name : ''}
                                        </td>
                                        <td style={{ padding: '8px 12px' }}
                                            title={PROPERTY_LABELS[r.property] ? r.property : undefined}>
                                            {propLabel(r.property, r.ieee)}
                                        </td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 'bold', fontFamily: 'monospace', fontSize: '14px' }}
                                            title={r.enumValues?.length
                                                ? (() => {
                                                    const lines = r.enumValues.map(e => `${e.value}: ${e.text}`);
                                                    const currentInList = r.numericValue == null || r.enumValues.some(e => Number(e.value) === r.numericValue);
                                                    if (!currentInList && r.numericValue != null)
                                                        lines.unshift(`${r.numericValue}: ${r.displayStr}  ← current`);
                                                    return lines.join('\n');
                                                })()
                                                : undefined}>
                                            {r.displayStr}
                                            {r.numericValue !== null && !r.enumValues?.length && r.displayStr !== String(r.numericValue) && (
                                                <span style={{ color: '#aaa', fontWeight: 'normal', fontSize: '11px', marginLeft: '6px' }}>({r.numericValue})</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '8px 12px', color: '#888', fontSize: '12px' }}>{r.unit}</td>
                                        <td style={{ padding: '8px 12px', fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>
                                            {formatDate(r.timestamp, timezone ?? undefined)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#aaa', textAlign: 'right' }}>
                        Auto-refreshes every 30 s
                    </div>
                </div>
            )}

            {/* ── Date Range & Export ──────────────────────────────── */}
            <div style={{ ...sectionStyle, border: '1px solid #6f42c1', backgroundColor: '#faf8ff' }}>
                <h3 style={{ margin: '0 0 14px 0', fontSize: '15px' }}>📅 Period och export</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label style={labelStyle}>Från</label>
                        <input type="datetime-local" style={{ ...inputStyle, width: 'auto' }}
                            value={logFrom} onChange={e => onLogFromChange(e.target.value)} />
                    </div>
                    <div>
                        <label style={labelStyle}>Till</label>
                        <input type="datetime-local" style={{ ...inputStyle, width: 'auto' }}
                            value={logTo} onChange={e => onLogToChange(e.target.value)} />
                    </div>
                    <button onClick={onLoadHistoryData}
                        disabled={selectedDevices.size === 0 || historyLoading}
                        style={btnStyle(selectedDevices.size === 0 ? '#aaa' : '#007bff')}>
                        {historyLoading ? 'Hämtar…' : '📊 Visa diagram'}
                    </button>
                    <button onClick={exportForAI}
                        disabled={selectedDevices.size === 0}
                        style={btnStyle(selectedDevices.size === 0 ? '#aaa' : '#6f42c1')}
                        title="Exportera aktuella värden och historik som text en AI kan läsa">
                        🤖 Exportera för AI
                    </button>
                    <button onClick={exportDeviceInfo}
                        disabled={selectedDevices.size === 0}
                        style={btnStyle(selectedDevices.size === 0 ? '#aaa' : '#17a2b8')}
                        title="Visa enhetsnamn och lista över mätvärden">
                        📋 Enhetsinfo
                    </button>
                </div>
            </div>

            {/* ── Historical Chart ─────────────────────────────────── */}
            <div style={sectionStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, fontSize: '15px' }}>📈 Historik</h3>
                    {chartData.length > 0 && chartSeries.length > 0 && (
                        <button
                            onClick={() => setChartFullscreen(true)}
                            style={{ padding: '4px 10px', fontSize: '12px', background: '#f0f2f5', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}
                            title="Visa diagrammet i helskärm"
                        >
                            ⛶ Helskärm
                        </button>
                    )}
                </div>

                {selectedDevices.size === 0 ? (
                    <p style={{ color: '#888', textAlign: 'center', padding: '32px 0', margin: 0 }}>
                        Välj en eller flera enheter ovanför och klicka sedan på ”Visa diagram”.
                    </p>
                ) : historyLoading ? (
                    <p style={{ color: '#888', textAlign: 'center', padding: '32px 0', margin: 0 }}>Hämtar…</p>
                ) : historyError ? (
                    <p style={{ color: '#c0392b', textAlign: 'center', padding: '32px 0', margin: 0 }}>{historyError}</p>
                ) : chartData.length === 0 ? (
                    <p style={{ color: '#888', textAlign: 'center', padding: '32px 0', margin: 0 }}>
                        Ingen data ännu — välj en period och klicka på ”Visa diagram”.
                    </p>
                ) : chartSeries.length === 0 ? (
                    <p style={{ color: '#888', textAlign: 'center', padding: '32px 0', margin: 0 }}>
                        Inga numeriska mätvärden hittades bland de valda.
                    </p>
                ) : (
                    <>
                        {chartSeries.length > 10 && (
                            <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#888', textAlign: 'center' }}>
                                Visar 10 av {chartSeries.length} kurvor. Avmarkera några mätvärden för att se de andra.
                            </p>
                        )}
                        <ResponsiveContainer width="100%" height={420}>
                            {renderLineChart()}
                        </ResponsiveContainer>
                    </>
                )}
            </div>

            {/* ── Avvikelse mot börvärde ───────────────────────────── */}
            {chartData.length > 0 && numericSeries.length > 0 && (
                <div style={sectionStyle}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '15px' }}>🎯 Avvikelse mot börvärde</h3>
                    <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#888' }}>
                        Period: {formatInTZ(new Date(logFrom).getTime(), timezone)} – {formatInTZ(new Date(logTo).getTime(), timezone)}.
                        Ändra börvärdet i rutan för att se avvikelsen mot ett annat värde (sparas inte).
                    </p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#f4f4f5' }}>
                                {['Mätvärde', 'Börvärde', 'Snitt', 'Avvikelse (snitt)'].map(h => (
                                    <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, borderBottom: '1px solid #ddd' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {numericSeries.map(key => {
                                const { ieee, property } = parseSeriesKey(key);
                                const raw = targetOverrides[key];
                                const hasTarget = raw !== undefined && raw !== '' && Number.isFinite(Number(raw.replace(',', '.')));
                                const target = hasTarget ? Number(raw.replace(',', '.')) : NaN;
                                const dev = hasTarget ? computeDeviation(seriesValues[key] ?? [], target) : null;
                                return (
                                    <tr key={key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '6px 10px' }}>{propLabel(property, ieee)}</td>
                                        <td style={{ padding: '6px 10px' }}>
                                            <input
                                                type="text"
                                                value={raw ?? ''}
                                                placeholder="—"
                                                onChange={e => setTargetOverrides(prev => ({ ...prev, [key]: e.target.value }))}
                                                style={{ ...inputStyle, width: 80 }}
                                            />
                                        </td>
                                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>
                                            {dev && dev.avg !== null ? dev.avg.toFixed(2) : '—'}
                                        </td>
                                        <td style={{ padding: '6px 10px', fontWeight: 700 }}>
                                            {dev ? formatDeviation(dev.devAvg, dev.devNull) : 'n/a'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Fullscreen chart modal ───────────────────────────── */}
            {chartFullscreen && (
                <div className="chart-fullscreen-overlay" ref={fullscreenOverlayRef}>
                    <div className="chart-fullscreen-header">
                        <span>📈 Historik</span>
                        <button className="chart-fullscreen-close" onClick={() => setChartFullscreen(false)}>✕ Stäng</button>
                    </div>
                    <div className="chart-rotate-hint">
                        <span>↻</span>
                        <span>Vrid telefonen för ett bredare diagram</span>
                    </div>
                    <div className="chart-fullscreen-body">
                        <ResponsiveContainer width="100%" height="100%">
                            {renderLineChart()}
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

        </div>
    );
};

export default MonitorTab;
